// src/pages/Usage.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import ResetFiltersButton, { filtersDiffer } from '../components/ResetFiltersButton';
import EmptyState from '../components/EmptyState';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import MultiSearchSelect from '../components/MultiSearchSelect';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { useServerPages } from '../components/Pager';
import { filterParams } from '../utils/api';
import { timeZone, tzLabel } from '../utils/timeZone';
import { TAB, TAB_ON, TAB_ROW } from '../theme/tabs';
import TeamFilter, { useClientTeams, teamText } from '../components/TeamFilter';
import { PAGE_TITLE } from '../theme/tabs';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { useClientWord } from '../hooks/useClientWord';

// ── Shared utilities ──────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: timeZone(), month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) + ' ' + tzLabel(new Date(iso));
}

function formatMonth(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function formatNum(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

function formatFax(num) {
  if (!num) return '—';
  const n = String(num).replace(/\D/g, '');
  if (n.length === 11) return `+${n[0]} (${n.slice(1,4)}) ${n.slice(4,7)}-${n.slice(7)}`;
  if (n.length === 10) return `+1 (${n.slice(0,3)}) ${n.slice(3,6)}-${n.slice(6)}`;
  return num;
}

const STATUS_COLORS = {
  delivered: { bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  failed   : { bg: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)' },
  queued   : { bg: 'var(--c-e3f2fd)', color: 'var(--t-1565c0)' },
  requeued : { bg: 'var(--c-fff8e1)', color: 'var(--t-e65100)' },
  received : { bg: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || { bg: 'var(--c-f5f5f5)', color: 'var(--t-666666)' };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
      {status}
    </span>
  );
}

// 3.40.1: the portal's shared loading look (components/Loader)
function LoadingSpinner() {
  return <Loader label="Loading…" />;
}

const OUTGOING_USAGE_DEFAULTS = { startDate: '', endDate: '', fromEmail: [], toNumber: [], status: '', limit: '50', account: '', team: '' };
const INCOMING_USAGE_DEFAULTS = { startDate: '', endDate: '', fromNumber: [], toNumber: [], incomingStatus: '', limit: '50', account: '', team: '' };

// Account filter on Outgoing / Incoming Fax Usage (3.18.0): Super Admins on
// "All accounts" can narrow the list to one account without switching the
// portal to it (sent as ?account=<id>). Applies as soon as it's picked.
function AccountFilter({ value, onPick }) {
  const { effectiveInfo, viewAs } = useAuth();
  const info = effectiveInfo || {};
  const allAccounts = !info.accountId || info.accountId === 'all';   // user-info sends 'all' on All accounts
  if (info.level !== 'super_admin' || !allAccounts || viewAs?.readOnly) return null;
  return (
    <div style={S.filterGroup}><label style={S.filterLabel}>Client</label>
      <select value={value} onChange={e => onPick(e.target.value)} style={S.filterInput}>
        <option value="">All clients</option>
        {(info.accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select></div>
  );
}

// ── Outgoing Usage Tab ────────────────────────────────────────
function OutgoingUsage() {
  const { getToken, can }          = useAuth();
  const { notify }            = useNotify();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState(OUTGOING_USAGE_DEFAULTS);
  const [applied, setApplied] = useState(OUTGOING_USAGE_DEFAULTS); // filters of the last search
  const clientTeams = useClientTeams();   // Team filter and column (3.19.5)
  const showTeam = clientTeams.length > 0 || data.some(f => (f.teamNames || []).length);
  const { sorted: sortedData, sort, onSort } = useSort(data, {
    team: teamText, date: f => f.sentAt || '', from: f => f.fromEmail, to: f => String(f.toNumber || '').replace(/\D/g, ''), file: f => f.fileName,
    pages: f => Number(f.pages) || 0, delivered: f => f.deliveredAt || '', status: f => f.status,
  });
  // Page by page (3.26.0): the server sends one page; sorting or Last loads all
  const pages = useServerPages(sortedData, applied.limit || '50', req => load(applied, req));
  const sortAll = key => { onSort(key); pages.sortAll(); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function load(f = filters, req = null) {
    setLoading(true); setError('');
    try {
      const token  = await getToken();
      const params = filterParams(f);
      const pp = pages.params(req, f.limit || '50');   // Show (3.16.0) = rows per page (3.26.0)
      params.set('limit', pp.limit); params.set('page', String(pp.page));
      const res  = await fetch(`${API_BASE}/portal/outgoing?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setData(json.data || []);
      pages.loaded(req, json.hasMore, f.limit || '50');
      setApplied(f);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  function resetFilters() { setFilters(OUTGOING_USAGE_DEFAULTS); load(OUTGOING_USAGE_DEFAULTS); }

  async function download() {
    try {
      const token  = await getToken();
      const params = filterParams(filters);
      const res  = await fetch(`${API_BASE}/portal/usage/outgoing/download?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `Sent_Fax_Usage_${new Date().toISOString().slice(0,10)}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  const totalFaxes = data.length;
  const totalPages = data.reduce((s, d) => s + (d.pages || 0), 0);
  const delivered  = data.filter(d => d.status === 'delivered').length;
  const failed     = data.filter(d => d.status === 'failed').length;

  return (
    <div>
      {/* Summary */}
      {data.length > 0 && (
        <div style={S.cards}>
          <div style={S.card}><div style={S.cardValue}>{formatNum(totalFaxes)}</div><div style={S.cardLabel}>Total Faxes</div></div>
          <div style={S.card}><div style={S.cardValue}>{formatNum(totalPages)}</div><div style={S.cardLabel}>Total Pages</div></div>
          <div style={{ ...S.card, borderColor: 'var(--b-c8e6c9)' }}><div style={{ ...S.cardValue, color: 'var(--t-2e7d32)' }}>{formatNum(delivered)}</div><div style={S.cardLabel}>Delivered</div></div>
          <div style={{ ...S.card, borderColor: 'var(--c-ffcdd2)' }}><div style={{ ...S.cardValue, color: 'var(--t-b71c1c)' }}>{formatNum(failed)}</div><div style={S.cardLabel}>Failed</div></div>
        </div>
      )}

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.filterRow}>
          <div style={S.filterGroup}><label style={S.filterLabel}>From Date</label>
            <input type="date" value={filters.startDate} onChange={e => setFilters(p=>({...p,startDate:e.target.value}))} style={S.filterInput} /></div>
          <div style={S.filterGroup}><label style={S.filterLabel}>To Date</label>
            <input type="date" value={filters.endDate} onChange={e => setFilters(p=>({...p,endDate:e.target.value}))} style={S.filterInput} /></div>
          <AccountFilter value={filters.account} onPick={v => { const f = { ...filters, account: v }; setFilters(f); load(f); }} />
          <TeamFilter teams={clientTeams} value={filters.team} onChange={v => setFilters(p => ({ ...p, team: v }))}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={S.filterGroup}><label style={S.filterLabel}>Sent By (Email)</label>
            <MultiSearchSelect suggestType="outgoing" field="fromEmail"
              values={filters.fromEmail} onChange={v => setFilters(p=>({...p,fromEmail:v}))}
              placeholder="user@domain.com" /></div>
          <div style={S.filterGroup}><label style={S.filterLabel}>Sent To (Fax #)</label>
            <MultiSearchSelect suggestType="outgoing" field="toNumber"
              values={filters.toNumber} onChange={v => setFilters(p=>({...p,toNumber:v}))}
              placeholder="e.g. 18005551234" /></div>
          <div style={S.filterGroup}><label style={S.filterLabel}>Status</label>
            <select value={filters.status} onChange={e => setFilters(p=>({...p,status:e.target.value}))} style={S.filterInput}>
              <option value="">All</option>
              <option value="queued">Queued</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
            </select></div>
          <ShowSelect value={filters.limit} onChange={v => setFilters(p => ({ ...p, limit: v }))}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={{ ...S.filterGroup, flexDirection: 'row', gap: 8, alignSelf: 'flex-end' }}>
            <button onClick={() => load()} style={S.searchBtn}><Emo e="🔍" /> Search</button>
            <ResetFiltersButton dirty={filtersDiffer(filters, OUTGOING_USAGE_DEFAULTS)}
              onReset={resetFilters} /></div>
        </div>
      </div>

      {/* Action bar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span style={{ fontSize:14, color:'var(--t-546e7a)' }}>{loading ? 'Loading...' : `${data.length} fax${data.length!==1?'es':''} found`}</span>
        {can('fax.reports') && <button onClick={download} style={S.downloadBtn}><Emo e="⬇" /> Download (.xlsx)</button>}
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="date" label="Date & Time" sort={sort} onSort={sortAll} style={S.th} />
            {showTeam && <SortTh k="team" label="Team" sort={sort} onSort={sortAll} style={S.th} />}
            <SortTh k="from" label="Sent By" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="to" label="Sent To (Fax #)" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="file" label="File Name" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="pages" label="Pages" sort={sort} onSort={sortAll} style={{ ...S.th, textAlign:'center' }} />
            <SortTh k="delivered" label="Delivered At" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="status" label="Status" sort={sort} onSort={sortAll} style={S.th} />
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={S.emptyCell}><LoadingSpinner /></td></tr>
            : data.length === 0 ? <tr><td colSpan={8} style={S.emptyCell}><EmptyState icon="📤" filtered={filtersDiffer(applied, OUTGOING_USAGE_DEFAULTS)} onClear={resetFilters}
                filteredText="No sent faxes match these filters." emptyText="No sent faxes yet." /></td></tr>
            : pages.pageRows.map((f,i) => (
              <tr key={f.faxId} style={{ ...S.tr, background: i%2===0?'var(--c-fafafa)':'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontSize:12, color:'var(--t-546e7a)', whiteSpace:'nowrap' }}>{formatDate(f.sentAt)}</td>
                {showTeam && <td style={S.td}>{teamText(f) || '—'}</td>}
                <td style={{ ...S.td, color:'var(--t-00695c)', fontWeight:500 }}>{f.fromEmail}</td>
                <td style={{ ...S.td, fontFamily:'monospace' }}>{formatFax(f.toNumber)}</td>
                <td style={{ ...S.td, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={f.fileName}>{f.fileName}</td>
                <td style={{ ...S.td, textAlign:'center' }}>{f.pages || '—'}</td>
                <td style={{ ...S.td, fontSize:12, color:'var(--t-546e7a)', whiteSpace:'nowrap' }}>{formatDate(f.deliveredAt)}</td>
                <td style={S.td}><StatusBadge status={f.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && <Pager {...pages.pager} label="faxes" />}
    </div>
  );
}

// ── Incoming Usage Tab ────────────────────────────────────────
function IncomingUsage() {
  const { getToken, can }          = useAuth();
  const { notify }            = useNotify();
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState(INCOMING_USAGE_DEFAULTS);
  const [applied, setApplied] = useState(INCOMING_USAGE_DEFAULTS); // filters of the last search
  const clientTeams = useClientTeams();   // Team filter and column (3.19.5)
  const showTeam = clientTeams.length > 0 || data.some(f => (f.teamNames || []).length);
  const { sorted: sortedData, sort, onSort } = useSort(data, {
    team: teamText, date: f => f.receivedAt || f.failedAt || '', from: f => String(f.fromNumber || '').replace(/\D/g, ''), to: f => String(f.toNumber || '').replace(/\D/g, ''),
    file: f => f.fileName, pages: f => Number(f.pages) || 0, status: f => f.status || (f.failedIncoming ? 'failed' : ''),
  });
  // Page by page (3.26.0): the server sends one page; sorting or Last loads all
  const pages = useServerPages(sortedData, applied.limit || '50', req => load(applied, req));
  const sortAll = key => { onSort(key); pages.sortAll(); };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function load(f = filters, req = null) {
    setLoading(true); setError('');
    try {
      const token  = await getToken();
      const params = filterParams(f);
      const pp = pages.params(req, f.limit || '50');   // Show (3.16.0) = rows per page (3.26.0)
      params.set('limit', pp.limit); params.set('page', String(pp.page));
      const res  = await fetch(`${API_BASE}/portal/incoming?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed');
      setData(json.data || []);
      pages.loaded(req, json.hasMore, f.limit || '50');
      setApplied(f);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  function resetFilters() { setFilters(INCOMING_USAGE_DEFAULTS); load(INCOMING_USAGE_DEFAULTS); }

  async function download() {
    try {
      const token  = await getToken();
      const params = filterParams(filters);
      const res  = await fetch(`${API_BASE}/portal/usage/incoming/download?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `Received_Fax_Usage_${new Date().toISOString().slice(0,10)}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  const totalFaxes = data.length;
  const totalPages = data.reduce((s, d) => s + (d.pages || 0), 0);

  return (
    <div>
      {/* Summary */}
      {data.length > 0 && (
        <div style={{ ...S.cards, gridTemplateColumns: 'repeat(2,1fr)' }}>
          <div style={S.card}><div style={S.cardValue}>{formatNum(totalFaxes)}</div><div style={S.cardLabel}>Total Faxes</div></div>
          <div style={S.card}><div style={S.cardValue}>{formatNum(totalPages)}</div><div style={S.cardLabel}>Total Pages</div></div>
        </div>
      )}

      {/* Filters */}
      <div style={S.filterBar}>
        <div style={S.filterRow}>
          <div style={S.filterGroup}><label style={S.filterLabel}>From Date</label>
            <input type="date" value={filters.startDate} onChange={e => setFilters(p=>({...p,startDate:e.target.value}))} style={S.filterInput} /></div>
          <div style={S.filterGroup}><label style={S.filterLabel}>To Date</label>
            <input type="date" value={filters.endDate} onChange={e => setFilters(p=>({...p,endDate:e.target.value}))} style={S.filterInput} /></div>
          <AccountFilter value={filters.account} onPick={v => { const f = { ...filters, account: v }; setFilters(f); load(f); }} />
          <TeamFilter teams={clientTeams} value={filters.team} onChange={v => setFilters(p => ({ ...p, team: v }))}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={S.filterGroup}><label style={S.filterLabel}>Status</label>
            <select value={filters.incomingStatus} onChange={e => setFilters(p=>({...p,incomingStatus:e.target.value}))} style={S.filterInput}>
              <option value="">All</option>
              <option value="received">Received</option>
              <option value="failed">Failed</option>
            </select></div>
          <div style={S.filterGroup}><label style={S.filterLabel}>From Number</label>
            <MultiSearchSelect suggestType="incoming" field="fromNumber"
              values={filters.fromNumber} onChange={v => setFilters(p=>({...p,fromNumber:v}))}
              placeholder="e.g. 18005551234" /></div>
          <div style={S.filterGroup}><label style={S.filterLabel}>To Number</label>
            <MultiSearchSelect suggestType="incoming" field="toNumber"
              values={filters.toNumber} onChange={v => setFilters(p=>({...p,toNumber:v}))}
              placeholder="e.g. 17622620442" /></div>
          <ShowSelect value={filters.limit} onChange={v => setFilters(p => ({ ...p, limit: v }))}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />
          <div style={{ ...S.filterGroup, flexDirection: 'row', gap: 8, alignSelf: 'flex-end' }}>
            <button onClick={() => load()} style={S.searchBtn}><Emo e="🔍" /> Search</button>
            <ResetFiltersButton dirty={filtersDiffer(filters, INCOMING_USAGE_DEFAULTS)}
              onReset={resetFilters} /></div>
        </div>
      </div>

      {/* Action bar */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span style={{ fontSize:14, color:'var(--t-546e7a)' }}>{loading ? 'Loading...' : `${data.length} fax${data.length!==1?'es':''} found`}</span>
        {can('fax.reports') && <button onClick={download} style={S.downloadBtn}><Emo e="⬇" /> Download (.xlsx)</button>}
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="date" label="Received At" sort={sort} onSort={sortAll} style={S.th} />
            {showTeam && <SortTh k="team" label="Team" sort={sort} onSort={sortAll} style={S.th} />}
            <SortTh k="from" label="From Number" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="to" label="To Number" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="file" label="File Name" sort={sort} onSort={sortAll} style={S.th} />
            <SortTh k="pages" label="Pages" sort={sort} onSort={sortAll} style={{ ...S.th, textAlign:'center' }} />
            <SortTh k="status" label="Status" sort={sort} onSort={sortAll} style={S.th} />
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={S.emptyCell}><LoadingSpinner /></td></tr>
            : data.length === 0 ? <tr><td colSpan={7} style={S.emptyCell}><EmptyState icon="📠" filtered={filtersDiffer(applied, INCOMING_USAGE_DEFAULTS)} onClear={resetFilters}
                filteredText="No received faxes match these filters." emptyText="No received faxes yet." /></td></tr>
            : pages.pageRows.map((f,i) => (
              <tr key={f.faxId} style={{ ...S.tr, background: i%2===0?'var(--c-fafafa)':'var(--c-ffffff)' }}>
                <td style={{ ...S.td, fontSize:12, color:'var(--t-546e7a)', whiteSpace:'nowrap' }}>{formatDate(f.receivedAt)}</td>
                {showTeam && <td style={S.td}>{teamText(f) || '—'}</td>}
                <td style={{ ...S.td, fontFamily:'monospace' }}>{formatFax(f.fromNumber)}</td>
                <td style={{ ...S.td, fontFamily:'monospace' }}>{formatFax(f.toNumber)}</td>
                <td style={{ ...S.td, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={f.fileName}>{f.fileName}</td>
                <td style={{ ...S.td, textAlign:'center' }}>{f.pages || '—'}</td>
                <td style={S.td} title={f.failureReason || ''}><StatusBadge status={f.status || 'received'} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && <Pager {...pages.pager} label="faxes" />}
    </div>
  );
}

// ── Monthly Billing Usage Tab (parametrized: team or private) ─
function MonthlyUsage({
  endpoint        = '/usage',
  downloadEndpoint = '/usage/download',
  heading         = 'Fax Usage Report',
  downloadName    = 'Crazy_Pixels_Fax_Usage',
} = {}) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can } = useAuth();
  const { notify }              = useNotify();
  const [allRecords, setAllRecords] = useState([]);
  // "All accounts" (3.11.1): a summary per account instead of one report;
  // otherwise the report names its account
  const [summary, setSummary]   = useState(null);
  const [accountName, setAccountName] = useState('');
  // An account opened from the "All accounts" summary (3.17.0): its report,
  // shown here without switching the portal to that account
  const [opened, setOpened] = useState(null);
  // Split by team (3.19.9): off by default; each month's total shared out
  // by each team's faxes and pages (display only)
  const clientTeamsU = useClientTeams();
  const [split, setSplit] = useState(false);
  const [teamSplit, setTeamSplit] = useState(null);
  useEffect(() => { setSplit(false); setTeamSplit(null); }, [opened]);   // another client: split again
  async function toggleSplit() {
    const on = !split;
    setSplit(on);
    if (on && !teamSplit) {
      try {
        const token = await getToken();
        const q = opened ? `?account=${encodeURIComponent(opened)}` : '';
        const res = await fetch(`${API_BASE}/usage/teams${q}`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Failed');
        setTeamSplit(json);
      } catch (e) { notify({ tone: 'error', title: "Couldn't split by team", detail: e.message }); setSplit(false); }
    }
  }

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const currentYear             = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadUsage(); }, []);

  // Filter records by selected year
  const records = allRecords.filter(r => {
    if (!r.billingMonth) return false;
    return new Date(r.billingMonth).getFullYear() === selectedYear;
  });

  // Get available years from records
  const availableYears = [...new Set(allRecords.map(r => {
    if (!r.billingMonth) return null;
    return new Date(r.billingMonth).getFullYear();
  }).filter(Boolean))].sort((a,b) => b-a);

  // Always include current year even if no records yet
  if (!availableYears.includes(currentYear)) availableYears.unshift(currentYear);

  async function loadUsage(acc = opened) {
    setLoading(true); setError('');
    try {
      const token = await getToken();
      const q     = acc ? `?account=${encodeURIComponent(acc)}` : '';
      const res   = await fetch(`${API_BASE}${endpoint}${q}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data  = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load usage');
      setAllRecords(data.data || []);
      setSummary(data.scope === 'all' ? data.summary : null);
      setAccountName(data.account?.name || '');

    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleDownload() {
    try {
      const token = await getToken();
      const acc   = opened ? `&account=${encodeURIComponent(opened)}` : '';
      const res   = await fetch(`${API_BASE}${downloadEndpoint}?year=${selectedYear}${acc}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = summary ? `Fax_Usage_All_Accounts_${new Date().toISOString().slice(0, 10)}.xlsx`
        : `${downloadName}${accountName ? `_${accountName.replace(/\W+/g, '_')}` : ''}_${selectedYear}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  const ytd = records.filter(r => !r.isCurrentMonth || selectedYear === currentYear).reduce((acc,r) => ({
    inboundPages : acc.inboundPages  + (r.inboundPages  || 0),
    sentPages    : acc.sentPages     + (r.sentPages     || 0),
    totalPages   : acc.totalPages    + (r.totalPages    || 0),
    inboundFaxes : acc.inboundFaxes  + (r.inboundFaxes  || 0),
    sentFaxes    : acc.sentFaxes     + (r.sentFaxes     || 0),
    totalFaxes   : acc.totalFaxes    + (r.totalFaxes    || 0),
  }), { inboundPages:0, sentPages:0, totalPages:0, inboundFaxes:0, sentFaxes:0, totalFaxes:0 });

  // Column sorting (3.16.0); the year's total row stays last
  const { sorted: sortedRecords, sort, onSort } = useSort(records, {
    month: r => r.billingMonth || '', start: r => r.periodStart || '', end: r => r.periodEnd || '',
    inPages: r => r.inboundPages || 0, sentPages: r => r.sentPages || 0, totalPages: r => r.totalPages || 0,
    inFaxes: r => r.inboundFaxes || 0, sentFaxes: r => r.sentFaxes || 0, totalFaxes: r => r.totalFaxes || 0,
  });

  if (loading) return <LoadingSpinner />;
  if (summary) return <AllAccountsUsage summary={summary} error={error} onDownload={handleDownload} onRefresh={() => loadUsage()} onPick={id => { setOpened(id); loadUsage(id); }} />;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
        <div>
          {opened && (
            <button onClick={() => { setOpened(null); setSelectedYear(currentYear); loadUsage(null); }} style={S.backLink}>← All clients</button>
          )}
          <h3 style={{ margin:'0 0 4px', color:'var(--t-00695c)', fontSize:16, fontWeight:700 }}>{heading}{accountName ? ` — ${accountName}` : ''}</h3>
          <p style={{ margin:0, color:'var(--t-78909c)', fontSize:13 }}><Emo e="🔒" /> Closed months are recorded and never change</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--c-e0e0e0)', fontSize:13, color:'var(--t-333333)', fontFamily:'inherit', background:'var(--c-ffffff)' }}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {can('fax.reports') && <button onClick={handleDownload} style={S.downloadBtn}><Emo e="⬇" /> Download (.xlsx)</button>}
          {(clientTeamsU.length > 0 || opened) && (
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--t-546e7a)', fontWeight:600, cursor:'pointer' }} title="Show each month split between the client's teams">
              <input type="checkbox" checked={split} onChange={toggleSplit} /> Split by team
            </label>
          )}
          <button onClick={() => loadUsage()} style={{ ...S.downloadBtn, background:'none', border:'1.5px solid var(--c-e0e0e0)', color:'var(--t-546e7a)' }}><Emo e="↻" /> Refresh</button>
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}
      {split && teamSplit && !teamSplit.teams.length && <div style={{ color:'var(--t-78909c)', fontSize:13, margin:'0 0 12px' }}>{clientWord === 'Client' ? 'This client has' : 'Your company has'} no teams, so there's nothing to split.</div>}

      {records.length > 0 && (
        <div style={S.cards}>
          <div style={S.card}><div style={S.cardValue}>{formatNum(ytd.totalFaxes)}</div><div style={S.cardLabel}>Total Faxes (YTD)</div></div>
          <div style={S.card}><div style={S.cardValue}>{formatNum(ytd.totalPages)}</div><div style={S.cardLabel}>Total Pages (YTD)</div></div>
          <div style={{ ...S.card, borderColor:'var(--c-bbdefb)' }}><div style={S.cardValue}>{formatNum(ytd.inboundFaxes)}</div><div style={S.cardLabel}>Inbound Faxes (YTD)</div></div>
          <div style={{ ...S.card, borderColor:'var(--c-bbdefb)' }}><div style={S.cardValue}>{formatNum(ytd.inboundPages)}</div><div style={S.cardLabel}>Inbound Pages (YTD)</div></div>
          <div style={{ ...S.card, borderColor: 'var(--b-c8e6c9)' }}><div style={S.cardValue}>{formatNum(ytd.sentFaxes)}</div><div style={S.cardLabel}>Sent Faxes (YTD)</div></div>
          <div style={{ ...S.card, borderColor: 'var(--b-c8e6c9)' }}><div style={S.cardValue}>{formatNum(ytd.sentPages)}</div><div style={S.cardLabel}>Sent Pages (YTD)</div></div>
        </div>
      )}

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="month" label="Month" sort={sort} onSort={onSort} style={S.th} />   {/* 3.54.0 (owner): no billing wording for clients */}
            <SortTh k="start" label="Period Start" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="end" label="Period End" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="inPages" label="Inbound Pages" sort={sort} onSort={onSort} style={{ ...S.th, background:'var(--c-1565c0)' }} />
            <SortTh k="sentPages" label="Sent Pages" sort={sort} onSort={onSort} style={{ ...S.th, background:'var(--c-2e7d32)' }} />
            <SortTh k="totalPages" label="Total Pages" sort={sort} onSort={onSort} style={{ ...S.th, background:'var(--c-37474f)' }} />
            <SortTh k="inFaxes" label="Inbound Faxes" sort={sort} onSort={onSort} style={{ ...S.th, background:'var(--c-1565c0)' }} />
            <SortTh k="sentFaxes" label="Sent Faxes" sort={sort} onSort={onSort} style={{ ...S.th, background:'var(--c-2e7d32)' }} />
            <SortTh k="totalFaxes" label="Total Faxes" sort={sort} onSort={onSort} style={{ ...S.th, background:'var(--c-37474f)' }} />
          </tr></thead>
          <tbody>
            {records.length === 0 ? (
              <tr><td colSpan={9} style={S.emptyCell}><div style={S.emptyState}><div style={{fontSize:36,marginBottom:10}}><Emo e="📈" /></div><div>No usage data available yet.</div></div></td></tr>
            ) : (
              <>
                {sortedRecords.map((r,i) => [
                  <tr key={r.billingMonth} style={{ ...S.tr, background: i%2===0?'var(--c-fafafa)':'var(--c-ffffff)' }}>
                    <td style={{ ...S.td, fontWeight:600, color:'var(--t-00695c)' }}>
                      {formatMonth(r.billingMonth)}
                      {r.isCurrentMonth && <span style={{ marginLeft:8, fontSize:11, background:'var(--c-e3f2fd)', color:'var(--t-1565c0)', padding:'2px 8px', borderRadius:20, fontWeight:700 }}>Live</span>}
                      {r.frozen && <span style={{ marginLeft:8, fontSize:12 }} title="Closed: recorded when the month ended and never changes"><Emo e="🔒" /></span>}
                      {r.closing && <span style={{ marginLeft:8, fontSize:11, background:'var(--c-e3f2fd)', color:'var(--t-1565c0)', padding:'2px 8px', borderRadius:20, fontWeight:700 }} title="Closed: recorded a day after it ends, so faxes finishing at the close are counted">Closing</span>}
                    </td>
                    <td style={S.td}>{r.periodStart ? r.periodStart.slice(0,10) : '—'}</td>
                    <td style={S.td}>{r.isCurrentMonth ? <span style={{color:'var(--t-1565c0)'}}>Today ({r.periodEnd?.slice(0,10)})</span> : r.periodEnd?.slice(0,10)}</td>
                    <td style={{ ...S.td, color:'var(--t-1565c0)', textAlign:'right' }}>{formatNum(r.inboundPages)}</td>
                    <td style={{ ...S.td, color:'var(--t-2e7d32)', textAlign:'right' }}>{formatNum(r.sentPages)}</td>
                    <td style={{ ...S.td, color:'var(--t-37474f)', fontWeight:700, textAlign:'right' }}>{formatNum(r.totalPages)}</td>
                    <td style={{ ...S.td, color:'var(--t-1565c0)', textAlign:'right' }}>{formatNum(r.inboundFaxes)}</td>
                    <td style={{ ...S.td, color:'var(--t-2e7d32)', textAlign:'right' }}>{formatNum(r.sentFaxes)}</td>
                    <td style={{ ...S.td, color:'var(--t-37474f)', fontWeight:700, textAlign:'right' }}>{formatNum(r.totalFaxes)}</td>
                  </tr>,
                  // Split by team: one line per team under the month (3.19.9)
                  ...(split && teamSplit ? (teamSplit.months[(r.billingMonth || '').slice(0, 7)] || []).map(t => (
                    <tr key={`${r.billingMonth}:${t.id || 'none'}`} style={{ background:'var(--c-f7fbfa)', borderBottom:'1px solid var(--c-eef5f3)' }}>
                      <td style={{ ...S.td, paddingLeft:28, fontSize:12, color:'var(--t-546e7a)' }} colSpan={5}>↳ {t.name}</td>
                      <td style={{ ...S.td, fontSize:12, color:'var(--t-37474f)', textAlign:'right' }}>{formatNum(t.pages)}</td>
                      <td style={S.td} colSpan={2} />
                      <td style={{ ...S.td, fontSize:12, color:'var(--t-37474f)', textAlign:'right' }}>{formatNum(t.faxes)}</td>
                    </tr>
                  )) : []),
                ])}
                <tr style={{ background:'var(--c-f0faf8)', borderTop:'2px solid var(--c-00897b)' }}>
                  <td style={{ ...S.td, fontWeight:700, color:'var(--t-00695c)' }} colSpan={3}>{selectedYear} YTD Total</td>
                  <td style={{ ...S.td, color:'var(--t-1565c0)', fontWeight:600, textAlign:'right' }}>{formatNum(ytd.inboundPages)}</td>
                  <td style={{ ...S.td, color:'var(--t-2e7d32)', fontWeight:600, textAlign:'right' }}>{formatNum(ytd.sentPages)}</td>
                  <td style={{ ...S.td, color:'var(--t-37474f)', fontWeight:700, textAlign:'right' }}>{formatNum(ytd.totalPages)}</td>
                  <td style={{ ...S.td, color:'var(--t-1565c0)', fontWeight:600, textAlign:'right' }}>{formatNum(ytd.inboundFaxes)}</td>
                  <td style={{ ...S.td, color:'var(--t-2e7d32)', fontWeight:600, textAlign:'right' }}>{formatNum(ytd.sentFaxes)}</td>
                  <td style={{ ...S.td, color:'var(--t-37474f)', fontWeight:700, textAlign:'right' }}>{formatNum(ytd.totalFaxes)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── "All accounts" (Super Admins, 3.11.1) ─────────────────────
// One row per account: this billing month (live) and the last one, each
// as that account's own report shows it, plus the combined total. Click an
// account to see its full report here (3.17.0: without switching to it).
const FORECAST_HELP = 'Forecast for the end of this billing period, by each client’s own billing cycle: the pace so far on weekdays, blended with last period (early in a period it leans on last period). Never below what’s already counted.';
function AllAccountsUsage({ summary, error, onDownload, onRefresh, onPick }) {
  const { can } = useAuth();   // Download only with "Download Excel reports" (3.36.0)
  const month = r => (r ? formatMonth(r.billingMonth) : '—');
  const { rows, total } = summary;
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <h3 style={{ margin:'0 0 4px', color:'var(--t-00695c)', fontSize:16, fontWeight:700 }}>Fax Usage — All clients</h3>
          <p style={{ margin:0, color:'var(--t-78909c)', fontSize:13 }}>
            Each client with its own billing. Click a client's name to see its full history here.
          </p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {can('fax.reports') && <button onClick={onDownload} style={S.downloadBtn}><Emo e="⬇" /> Download (.xlsx)</button>}
          <button onClick={onRefresh} style={{ ...S.downloadBtn, background:'none', border:'1.5px solid var(--c-e0e0e0)', color:'var(--t-546e7a)' }}><Emo e="↻" /> Refresh</button>
        </div>
      </div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={S.cards}>
        <div style={S.card}><div style={S.cardValue}>{formatNum(total.current.totalPages)}</div><div style={S.cardLabel}>Pages this month (all)</div></div>
        <div style={S.card}><div style={S.cardValue}>{formatNum(total.current.totalFaxes)}</div><div style={S.cardLabel}>Faxes this month (all)</div></div>
        <div style={S.card}><div style={S.cardValue}>{formatNum(total.last.totalPages)}</div><div style={S.cardLabel}>Pages last month (all)</div></div>
        <div style={S.card}><div style={S.cardValue}>{formatNum(total.last.totalFaxes)}</div><div style={S.cardLabel}>Faxes last month (all)</div></div>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <th style={S.th}>Client</th>
            <th style={S.th}>Billing</th>
            <th style={S.th}>This month</th>
            <th style={{ ...S.th, background:'var(--c-37474f)' }}>Pages</th>
            <th style={{ ...S.th, background:'var(--c-37474f)' }}>Faxes</th>
            {/* 3.31.0: where this billing period will end up, by each client's own cycle */}
            <th style={{ ...S.th, background:'var(--c-4527a0)' }} title={FORECAST_HELP}>Forecast pages</th>
            <th style={{ ...S.th, background:'var(--c-4527a0)' }} title={FORECAST_HELP}>Forecast faxes</th>
            <th style={S.th}>Last month</th>
            <th style={{ ...S.th, background:'var(--c-37474f)' }}>Pages</th>
            <th style={{ ...S.th, background:'var(--c-37474f)' }}>Faxes</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} style={{ ...S.tr, background: i%2===0?'var(--c-fafafa)':'var(--c-ffffff)' }}>
                <td style={S.td}>
                  <button onClick={() => onPick(r.id)} style={S.accountLink} title={`See ${r.name}'s usage history`}>{r.name}</button>
                  <div style={{ fontSize:11, color:'var(--t-78909c)' }}>{r.type === 'individual' ? 'Individual' : 'Business'}</div>
                </td>
                <td style={S.td}>{r.billing}</td>
                <td style={S.td}>{month(r.current)}{r.current && <span style={{ marginLeft:8, fontSize:11, background:'var(--c-e3f2fd)', color:'var(--t-1565c0)', padding:'2px 8px', borderRadius:20, fontWeight:700 }}>Live</span>}</td>
                <td style={{ ...S.td, fontWeight:700, textAlign:'right' }}>{formatNum(r.current?.totalPages ?? 0)}</td>
                <td style={{ ...S.td, fontWeight:700, textAlign:'right' }}>{formatNum(r.current?.totalFaxes ?? 0)}</td>
                <td style={{ ...S.td, color:'var(--t-4527a0)', textAlign:'right' }} title={r.periodEndsOn ? `By the end of this billing period (${r.periodEndsOn})` : undefined}>{r.forecast ? formatNum(r.forecast.totalPages) : '—'}</td>
                <td style={{ ...S.td, color:'var(--t-4527a0)', textAlign:'right' }} title={r.periodEndsOn ? `By the end of this billing period (${r.periodEndsOn})` : undefined}>{r.forecast ? formatNum(r.forecast.totalFaxes) : '—'}</td>
                <td style={S.td}>{month(r.last)}</td>
                <td style={{ ...S.td, textAlign:'right' }}>{r.last ? formatNum(r.last.totalPages) : '—'}</td>
                <td style={{ ...S.td, textAlign:'right' }}>{r.last ? formatNum(r.last.totalFaxes) : '—'}</td>
              </tr>
            ))}
            <tr style={{ background:'var(--c-f0faf8)', borderTop:'2px solid var(--c-00897b)' }}>
              <td style={{ ...S.td, fontWeight:700, color:'var(--t-00695c)' }} colSpan={3}>Total, all clients</td>
              <td style={{ ...S.td, fontWeight:700, textAlign:'right' }}>{formatNum(total.current.totalPages)}</td>
              <td style={{ ...S.td, fontWeight:700, textAlign:'right' }}>{formatNum(total.current.totalFaxes)}</td>
              <td style={{ ...S.td, fontWeight:700, color:'var(--t-4527a0)', textAlign:'right' }}>{formatNum(total.forecast?.totalPages ?? 0)}</td>
              <td style={{ ...S.td, fontWeight:700, color:'var(--t-4527a0)', textAlign:'right' }}>{formatNum(total.forecast?.totalFaxes ?? 0)}</td>
              <td style={S.td}></td>
              <td style={{ ...S.td, fontWeight:700, textAlign:'right' }}>{formatNum(total.last.totalPages)}</td>
              <td style={{ ...S.td, fontWeight:700, textAlign:'right' }}>{formatNum(total.last.totalFaxes)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Fax Usage Report ─────────────────────────────────────────
// The account picked at the top (3.10); "Private Fax Usage" (Jeremy's old
// tier) was retired in 3.11: his usage shows like any account's
function BillingReport() {
  return (
    <MonthlyUsage
      endpoint="/usage"
      downloadEndpoint="/usage/download"
      heading="Fax Usage"
      downloadName="Crazy_Pixels_Fax_Usage"
    />
  );
}

// ── Main Usage Component ──────────────────────────────────────
export default function Usage() {
  const { effectiveInfo, can } = useAuth();
  // Super Admins see every usage view (and the private line's usage);
  // roles with "usage details" (Roles & Access) also see the outgoing and
  // incoming usage views; everyone else sees the billing report
  const isAdmin    = effectiveInfo?.roleId === 'super_admin';
  const seeDetails = isAdmin || can('usage.details');

  const [subTab, setSubTabState] = useState('billing');   // opens on the report (3.14.0)
  function setSubTab(tab) {
    if (!seeDetails && tab !== 'billing') return;
    setSubTabState(tab);
  }

  // Only the report: shown on its own, without a lone tab button above it
  // (its "Team Fax Usage" heading already names it) (3.9.1)
  if (!seeDetails) return <div><h2 style={PAGE_TITLE} className="cp-title"><Emo e="👨🏻‍💻" /> Usage</h2><BillingReport /></div>;   // heading for everyone (3.19.10)

  return (
    <div>
      <h2 style={PAGE_TITLE} className="cp-title"><Emo e="👨🏻‍💻" /> Usage</h2>
      {/* Sub tabs */}
      <div style={S.subTabs} role="tablist" aria-label="Usage">
        {/* Fax Usage Report first (3.14.0), then the details; 3.42.3: tabs
            (the sliding highlight) with moving icons */}
        <button role="tab" aria-selected={subTab === 'billing'} onClick={() => setSubTab('billing')} style={{ ...S.subTab, ...(subTab==='billing' ? S.subTabActive : {}) }}>
          <Emo e="🧮" /> Fax Usage Report
        </button>
        <button role="tab" aria-selected={subTab === 'outgoing'} onClick={() => setSubTab('outgoing')} style={{ ...S.subTab, ...(subTab==='outgoing' ? S.subTabActive : {}) }}>
          <Emo e="📤" /> Sent Fax Usage
        </button>
        <button role="tab" aria-selected={subTab === 'incoming'} onClick={() => setSubTab('incoming')} style={{ ...S.subTab, ...(subTab==='incoming' ? S.subTabActive : {}) }}>
          <Emo e="📥" /> Received Fax Usage
        </button>
      </div>

      <div style={{ marginTop: 20 }}>
        {subTab === 'outgoing' && seeDetails && <OutgoingUsage />}
        {subTab === 'incoming' && seeDetails && <IncomingUsage />}
        {subTab === 'billing'  && <BillingReport />}
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────
const S = {
  backLink    : { background:'none', border:'none', padding:0, marginBottom:6, color:'var(--t-00897b)', fontWeight:600, fontSize:13, cursor:'pointer', fontFamily:'inherit' },
  accountLink : { background:'none', border:'none', padding:0, color:'var(--t-00695c)', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', textDecoration:'underline', textUnderlineOffset:3 },
  subTabs   : TAB_ROW,
  subTab    : TAB,
  subTabActive: TAB_ON,
  childTabs : { display:'flex', gap:4, borderBottom:'1px solid var(--c-eceff1)', marginBottom:4 },
  childTab  : { padding:'6px 16px', background:'none', border:'none', cursor:'pointer', fontSize:13, fontWeight:500, color:'var(--t-90a4ae)', borderBottom:'2px solid transparent', marginBottom:-1, fontFamily:'inherit' },
  childTabActive: { color:'var(--t-00695c)', borderBottom:'2px solid var(--c-00897b)', fontWeight:700 },
  cards     : { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:14, marginBottom:20 },
  card      : { background:'var(--c-ffffff)', borderRadius:12, padding:'16px 20px', boxShadow:'0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border:'1px solid var(--c-e0f2f1)', textAlign:'center' },
  cardValue : { fontSize:24, fontWeight:700, color:'var(--t-00695c)', marginBottom:4 },
  cardLabel : { fontSize:11, color:'var(--t-78909c)', textTransform:'uppercase', letterSpacing:'0.5px' },
  filterBar : { background:'var(--c-ffffff)', borderRadius:12, padding:'16px 20px', marginBottom:16, boxShadow:'0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', border:'1px solid var(--b-e8f5e9)' },
  filterRow : { display:'flex', flexWrap:'wrap', gap:14, alignItems:'flex-end' },
  filterGroup: { display:'flex', flexDirection:'column', gap:4, minWidth:150 },
  filterLabel: { fontSize:11, fontWeight:600, color:'var(--t-546e7a)', textTransform:'uppercase', letterSpacing:'0.5px' },
  filterInput: { padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--c-e0e0e0)', fontSize:13, color:'var(--t-333333)', fontFamily:'inherit' },
  searchBtn : { padding:'9px 18px', background:'var(--c-00897b)', color:'var(--t-ffffff)', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' },
  downloadBtn: { padding:'8px 16px', background:'var(--c-37474f)', color:'var(--t-ffffff)', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' },
  errorBanner: { background:'var(--c-fce4ec)', color:'var(--t-b71c1c)', padding:'12px 18px', borderRadius:8, marginBottom:16, fontSize:14 },
  tableWrap : { background:'var(--c-ffffff)', borderRadius:12, overflow:'auto', boxShadow:'0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border:'1px solid var(--b-e8f5e9)' },
  table     : { width:'100%', borderCollapse:'collapse' },
  thead     : { background:'var(--c-00695c)' },
  th        : { padding:'11px 14px', textAlign:'left', color:'var(--t-ffffff)', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', whiteSpace:'nowrap' },
  tr        : { borderBottom:'1px solid var(--c-f0f0f0)' },
  td        : { padding:'10px 14px', fontSize:13, color:'var(--t-333333)', verticalAlign:'middle' },
  emptyCell : { textAlign:'center', padding:'40px 0' },
  emptyState: { color:'var(--t-90a4ae)', fontSize:14 },
};